import { ArrowRight, FlaskConical, Info, Users, Watch } from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  Legend,
  Line,
  LineChart,
  ReferenceDot,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useDashboardChart } from "@/hooks/api";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TableCell, TableRow } from "@/components/ui/table";
import {
  DashboardTable,
  useDashboardTable,
  type DashboardTableColumn,
} from "./dashboard-table";
import { FiltersCard } from "./filters-card";
import { useDashboardUrlState } from "./useDashboardUrlState";
import {
  AGE_BANDS,
  GENDERS,
  TIERS,
  formatPortraitMonth,
} from "./customer-portrait-model";
import {
  COLORS,
  Distribution,
  Export,
  Field,
  MonthSelect,
  Panel,
  PortraitExportContext,
  Stat,
  number,
  percent,
  signed,
  type PortraitBucket,
} from "./customer-portrait-ui";

type Value = {
  count: number | null;
  denominator: number | null;
  percent: number | null;
};
type TopicRow = { label: string; total: Value; cells: PortraitBucket[] };
type Topic = {
  id: string;
  category: string;
  question: string;
  options: string[];
  rows: TopicRow[];
};
type Month = {
  month: string;
  population: number | null;
  cells: PortraitBucket[];
  choice: Value;
  option: string | null;
};
type Snapshot = {
  asOf: string;
  status: "ready" | "too_small";
  health: {
    enabled: boolean;
    notice: string;
    consent: string;
    minimumGroup: number;
    minimumCell: number;
    countStep: number;
  };
  summary: {
    population: number | null;
    answered?: Value;
    wearables?: Value;
    selfReported?: Value;
  };
  distributions: Record<string, PortraitBucket[]>;
  topics: Topic[];
  monthly: Month[];
  aminoAge: {
    count: number | null;
    first: number | null;
    latest: number | null;
    change: number | null;
    months: number | null;
  } | null;
};
const OVERLAP =
  "Privat og bedrift kan overlappe. «Begge» inngår i begge filtre; hver person telles én gang totalt. Bedrift betyr et registrert sponset besøk, ikke nåværende ansettelse eller aktivt medlemskap.";
const STATUS =
  "Aktiv privatkunde har et betalt, aktivt abonnement, også ved varslet oppsigelse. Tidligere abonnement betyr tidligere betalt abonnement uten aktive abonnementer. Abonnementsstatus prioriteres også for sponsede personer. Uten betalt abonnement vises historisk sponsede kunder og øvrige privatkunder separat, uten å anta at de er aktive eller churnede. Leads har ingen registrert betalende relasjon. Første dato bruker gjenopprettede abonnementsstarter og registrerte betalte/sponsede besøk.";
const ANSWERS =
  "Andel av personer med svar på spørsmålet, innen publiserbare personverngrupper. Flervalg kan summere til mer enn 100 %. Fritekst og ukjente alternativer blir «Annet». «—» betyr skjermet eller manglende grunnlag, aldri 0. Antall og grunnlag er avrundet til nærmeste 5.";
const display = (value: number | null | undefined, rate = false) =>
  value == null ? "—" : rate ? percent(value) : number(value);
const cellValue = (value: Value, measure: string) =>
  measure === "count" ? value.count : value.percent;
const csvValue = (value: number | null) =>
  value === null ? null : number(value);
const valueNote = (value: Value | undefined) =>
  value?.count == null || value.denominator == null
    ? "Skjermet eller ikke tilgjengelig"
    : `Ca. ${number(value.count)} av ${number(value.denominator)} · publiserbart grunnlag`;

function Needs({ topic, measure }: { topic: Topic; measure: string }) {
  const labels = topic.rows[0]?.cells.map((c) => c.label) ?? [];
  const columns: DashboardTableColumn<TopicRow>[] = [
    {
      id: "option",
      label: "Svaralternativ",
      value: (r) => r.label,
      headClassName: "min-w-64",
    },
    ...(measure === "count"
      ? [
          {
            id: "total",
            label: "Totalt",
            value: (r: TopicRow) => r.total.count,
            numeric: true,
            align: "right" as const,
          },
        ]
      : []),
    ...labels.map((label, index) => ({
      id: `group-${index}`,
      label,
      value: (r: TopicRow) =>
        r.cells[index] ? cellValue(r.cells[index]!, measure) : null,
      numeric: true,
      align: "right" as const,
    })),
  ];
  const table = useDashboardTable("portrait-needs", columns, topic.rows);
  return (
    <Panel
      title={topic.question}
      info={ANSWERS}
      subtitle={`${topic.category} · ${measure === "count" ? "Antall personer (avrundet)" : "Andel av besvarte"}`}
      action={
        <Export
          title={`behov-${topic.id}`}
          headers={[
            "Svaralternativ",
            ...(measure === "count" ? ["Totalt"] : []),
            ...labels.map(
              (l) => `${l} (${measure === "count" ? "antall" : "%"})`,
            ),
          ]}
          rows={table.viewRows.map((r) => [
            r.label,
            ...(measure === "count" ? [r.total.count] : []),
            ...r.cells.map((c) => csvValue(cellValue(c, measure))),
          ])}
        />
      }
    >
      <DashboardTable
        table={table}
        columns={columns}
        renderRow={(r) => (
          <TableRow key={r.label}>
            <TableCell className="max-w-80 whitespace-normal">
              {r.label}
            </TableCell>
            {measure === "count" && (
              <TableCell className="text-right font-semibold">
                {display(r.total.count)}
              </TableCell>
            )}
            {r.cells.map((c) => (
              <TableCell key={c.label} className="text-right tabular-nums">
                <span
                  className="inline-block min-w-16 rounded px-2 py-1"
                  style={
                    c.percent === null
                      ? undefined
                      : {
                          backgroundColor: `color-mix(in srgb, var(--color-admin-primary) ${Math.round(c.percent * 0.42)}%, transparent)`,
                        }
                  }
                >
                  {display(cellValue(c, measure), measure !== "count")}
                </span>
                <span className="block text-[10px] text-admin-text-subtle">
                  {valueNote(c)}
                </span>
              </TableCell>
            ))}
          </TableRow>
        )}
      />
      <p className="mt-3 text-xs text-admin-text-subtle">
        Mørkere felt = høyere andel. «—» er skjermet eller manglende grunnlag.
        CSV inneholder bare de samme publiserte verdiene; skjermede felt er
        tomme.
      </p>
    </Panel>
  );
}

function HealthProfile({ data }: { data: Snapshot }) {
  const pairs = data.aminoAge;
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Distribution
        title="BMI"
        rows={data.distributions.bmi ?? []}
        info="Siste registrerte høyde og vekt må være gyldige og høyst ett år fra hverandre. Høyde 80–250 cm, vekt 20–350 kg og beregnet BMI 8–100. Grunnlag: personer med gyldig BMI i publiserbare grupper, ikke hele kundeutvalget. BMI er ikke en diagnose."
        subtitle={
          data.health.enabled
            ? "Andel av gyldige BMI-observasjoner i publiserbare grupper"
            : "Helsedata deaktivert — avventer godkjent utrulling"
        }
      />
      <Panel
        title="Amino Age: første → siste aldersgap"
        info="Gap = Amino Age minus kronologisk alder. Samme personer med to ulike måledatoer og samme, kjent modellkonfigurasjon ved første og siste måling. Første måling er ikke nødvendigvis ved innmelding. Gap avgrenses til −20…20 år og gruppesnitt avrundes til 0,5 år. Dette er ikke en kausal effekt av Amino."
        subtitle={
          !data.health.enabled
            ? "Helsedata deaktivert — avventer godkjent utrulling"
            : pairs?.count
              ? `Ca. ${number(pairs.count)} personer med sammenlignbare observasjoner`
              : "For få personer til å vise helsedata — minimum 20 per fast gruppe"
        }
        action={
          <Export
            title="amino-age-par"
            headers={[
              "Personer",
              "Første gap (år)",
              "Siste gap (år)",
              "Endring (år)",
              "Gjennomsnittlig tid (mnd)",
            ]}
            rows={
              pairs?.count
                ? [
                    [
                      pairs.count,
                      csvValue(pairs.first),
                      csvValue(pairs.latest),
                      csvValue(pairs.change),
                      csvValue(pairs.months),
                    ],
                  ]
                : []
            }
          />
        }
      >
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-admin-bg p-5">
          <div>
            <p className="text-xs text-admin-text-subtle">Første registrerte</p>
            <p className="mt-1 text-3xl font-semibold">
              {signed(pairs?.first ?? null)} <span className="text-sm">år</span>
            </p>
          </div>
          <ArrowRight className="h-5 w-5 text-admin-text-subtle" />
          <div>
            <p className="text-xs text-admin-text-subtle">Siste registrerte</p>
            <p className="mt-1 text-3xl font-semibold">
              {signed(pairs?.latest ?? null)}{" "}
              <span className="text-sm">år</span>
            </p>
          </div>
        </div>
        <p className="mt-4 text-sm">
          Gjennomsnittlig endring:{" "}
          <strong>{signed(pairs?.change ?? null)} år</strong>
        </p>
        <p className="mt-1 text-xs text-admin-text-subtle">
          {pairs?.months == null
            ? "Skjermet eller manglende sammenlignbare målinger."
            : `Gjennomsnittlig ${number(pairs.months)} måneder mellom målingene.`}
        </p>
      </Panel>
    </div>
  );
}

function Monthly({
  rows,
  measure,
  needs,
  from,
  to,
}: {
  rows: Month[];
  measure: string;
  needs: boolean;
  from: string;
  to: string;
}) {
  const labels =
    rows.find((r) => r.cells.length)?.cells.map((c) => c.label) ?? [];
  const columns: DashboardTableColumn<Month>[] = [
    { id: "month", label: "Inngangsmåned", value: (r) => r.month },
    {
      id: "population",
      label: "Personer",
      value: (r) => r.population,
      numeric: true,
      align: "right",
    },
    ...(needs
      ? [
          {
            id: "answered",
            label: "Besvart",
            value: (r: Month) => r.choice.denominator,
            numeric: true,
            align: "right" as const,
          },
          {
            id: "selected",
            label: "Valgt alternativ",
            value: (r: Month) => r.choice.count,
            numeric: true,
            align: "right" as const,
          },
          {
            id: "percent",
            label: "Andel av besvarte",
            value: (r: Month) => r.choice.percent,
            numeric: true,
            align: "right" as const,
          },
        ]
      : labels.map((label, i) => ({
          id: `group-${i}`,
          label,
          value: (r: Month) =>
            r.cells[i] ? cellValue(r.cells[i]!, measure) : null,
          numeric: true,
          align: "right" as const,
        }))),
  ];
  const table = useDashboardTable(
    needs ? "portrait-monthly-need" : "portrait-months",
    columns,
    rows,
  );
  const headers = [
    "Inngangsmåned",
    "Personer",
    ...(needs
      ? ["Besvart", "Valgt alternativ", "Andel (%)"]
      : labels.map((l) => `${l} (${measure === "count" ? "antall" : "%"})`)),
  ];
  const csv = (source: Month[]) =>
    source.map((r) => [
      formatPortraitMonth(r.month),
      r.population,
      ...(needs
        ? [r.choice.denominator, r.choice.count, csvValue(r.choice.percent)]
        : r.cells.map((c) => csvValue(cellValue(c, measure)))),
    ]);
  const data = rows.map((r) => ({
    month: r.month,
    total: r.population,
    value: cellValue(r.choice, measure),
    ...Object.fromEntries(r.cells.map((c) => [c.label, cellValue(c, measure)])),
  }));
  // Never derive a total that would fill a screened month/cell.
  const total = rows.every((r) => r.population !== null)
    ? rows.reduce((sum, r) => sum + r.population!, 0)
    : null;
  const selected = rows.every((r) => r.choice.count !== null)
    ? rows.reduce((sum, r) => sum + r.choice.count!, 0)
    : null;
  const answered = rows.every((r) => r.choice.denominator !== null)
    ? rows.reduce((sum, r) => sum + r.choice.denominator!, 0)
    : null;
  const axis = (
    <>
      <CartesianGrid stroke="var(--color-admin-card-border)" vertical={false} />
      <XAxis
        dataKey="month"
        tickFormatter={(v) => formatPortraitMonth(String(v), true)}
        tick={{ fontSize: 11, fill: "var(--color-admin-text-subtle)" }}
      />
      <YAxis
        domain={measure === "count" ? [0, "auto"] : [0, 100]}
        tickFormatter={(v) => display(Number(v), measure !== "count")}
        tick={{ fontSize: 11, fill: "var(--color-admin-text-subtle)" }}
      />
      <Tooltip
        labelFormatter={(v) => formatPortraitMonth(String(v))}
        formatter={(v) =>
          display(v == null ? null : Number(v), measure !== "count")
        }
        contentStyle={{
          backgroundColor: "var(--color-admin-card)",
          borderColor: "var(--color-admin-card-border)",
        }}
      />
    </>
  );
  return (
    <div className="space-y-4">
      <p className="rounded-lg border border-admin-card-border p-4 text-sm text-admin-text-secondary">
        Inngangskohorter, ikke historiske aktive kunder. Status og relasjon
        gjelder i dag / siste kjente relasjon; alder gjelder ved inngang. Behov
        bruker siste svar målt, registrert og sist endret i de første 30 dagene
        fra inngang, ikke dagens svar. Hull betyr skjermet eller manglende
        grunnlag.
      </p>
      <Panel
        title={
          needs
            ? (rows[0]?.option ?? "Behov ved inngang")
            : "Hvem tiltrekker vi oss over tid?"
        }
        info={
          needs
            ? ANSWERS
            : "Søyler viser inngangskohorter. Skjermede felt er ikke 0. Avrundede delsummer kan avvike fra avrundet total. Endringer er ikke bevis for effekt av markedsføring eller produkt."
        }
        subtitle={`${formatPortraitMonth(from)} → ${formatPortraitMonth(to)} · avrundede, publiserte verdier`}
        action={
          <Export
            title={`måneder-${from}-${to}`}
            headers={headers}
            rows={csv(rows)}
          />
        }
      >
        <p className="mb-3 text-right text-sm">
          Totalt:{" "}
          <strong>
            {needs
              ? `${display(selected)} av ${display(answered)} besvarte`
              : display(total) + " personer"}
          </strong>
          {(needs ? selected === null || answered === null : total === null) &&
            " · skjermede måneder, total vises ikke"}
        </p>
        <div className="overflow-x-auto">
          <div
            className="h-80"
            style={{ minWidth: Math.max(600, rows.length * 64) }}
          >
            <ResponsiveContainer width="100%" height="100%">
              {needs ? (
                <LineChart
                  data={data}
                  margin={{ top: 24, right: 20, left: 0, bottom: 0 }}
                >
                  {axis}
                  <Line
                    dataKey="value"
                    name={rows[0]?.option ?? "Behov"}
                    stroke="var(--color-admin-primary)"
                    strokeWidth={2}
                    connectNulls={false}
                    isAnimationActive={false}
                  >
                    <LabelList
                      dataKey="value"
                      position="top"
                      formatter={(v) =>
                        v == null ? "" : display(Number(v), measure !== "count")
                      }
                      fill="var(--color-admin-text-primary)"
                      fontSize={11}
                    />
                  </Line>
                </LineChart>
              ) : (
                <BarChart
                  data={data}
                  margin={{ top: 24, right: 10, left: 0, bottom: 0 }}
                >
                  {axis}
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  {labels.map((label, i) => (
                    <Bar
                      key={label}
                      dataKey={label}
                      stackId="mix"
                      fill={COLORS[i % COLORS.length]}
                      maxBarSize={56}
                      isAnimationActive={false}
                    >
                      <LabelList
                        dataKey={label}
                        content={({ x, y, width, height, value }) =>
                          typeof x === "number" &&
                          typeof y === "number" &&
                          typeof width === "number" &&
                          typeof height === "number" &&
                          width >= 28 &&
                          height >= 18 &&
                          value != null &&
                          Number(value) > 0 ? (
                            <text
                              x={x + width / 2}
                              y={y + height / 2}
                              textAnchor="middle"
                              dominantBaseline="central"
                              fontSize={10}
                              fill="var(--color-admin-primary-fg)"
                            >
                              {display(Number(value), measure !== "count")}
                            </text>
                          ) : null
                        }
                      />
                    </Bar>
                  ))}
                  {rows
                    .filter((r) => r.population !== null)
                    .map((r) => (
                      <ReferenceDot
                        key={r.month}
                        x={r.month}
                        y={measure === "count" ? r.population! : 100}
                        r={0}
                        stroke="none"
                        label={{
                          value: `n≈${number(r.population!)}`,
                          position: "top",
                          offset: 8,
                          fill: "var(--color-admin-text-primary)",
                          fontSize: 11,
                        }}
                      />
                    ))}
                </BarChart>
              )}
            </ResponsiveContainer>
          </div>
        </div>
      </Panel>
      <Panel
        title="Månedsgrunnlag"
        info="Samme publiserte verdier som grafen. CSV følger sorteringen og beholder skjermede felt som tomme."
        action={
          <Export
            title={`månedsgrunnlag-${from}-${to}`}
            headers={headers}
            rows={csv(table.viewRows)}
          />
        }
      >
        <DashboardTable
          table={table}
          columns={columns}
          renderRow={(r) => (
            <TableRow key={r.month}>
              <TableCell>{formatPortraitMonth(r.month)}</TableCell>
              <TableCell className="text-right">
                {display(r.population)}
              </TableCell>
              {needs ? (
                <>
                  <TableCell className="text-right">
                    {display(r.choice.denominator)}
                  </TableCell>
                  <TableCell className="text-right">
                    {display(r.choice.count)}
                  </TableCell>
                  <TableCell className="text-right">
                    {display(r.choice.percent, true)}
                  </TableCell>
                </>
              ) : (
                r.cells.map((c) => (
                  <TableCell key={c.label} className="text-right">
                    {display(cellValue(c, measure), measure !== "count")}
                  </TableCell>
                ))
              )}
            </TableRow>
          )}
        />
      </Panel>
    </div>
  );
}

export function CustomerPortraitDashboard() {
  const today = new Date().toISOString().slice(0, 7);
  const [state, update] = useDashboardUrlState({
    tab: "overview",
    population: "",
    relationship: "",
    tier: "",
    gender: "",
    age: "",
    ageBasis: "today",
    topic: "goals",
    compare: "age",
    measure: "percent",
    from: `${Number(today.slice(0, 4)) - 1}-01`,
    to: today,
    trend: "age",
    trendOption: "Bedre søvn",
  });
  const tab = ["overview", "needs", "time"].includes(state.tab)
    ? state.tab
    : "overview";
  const {
    population,
    relationship,
    tier,
    gender,
    age,
    ageBasis,
    from,
    to,
    compare,
    trend,
    trendOption,
  } = state;
  const params = {
    population,
    relationship,
    tier,
    gender,
    age,
    ageBasis,
    from,
    to,
    compare,
    trend,
    trendOption,
  };
  const query = useDashboardChart<typeof params, Snapshot>(
    "customer-portrait",
    "snapshot",
    params,
  );
  const data = query.data?.[0];
  const topic =
    data?.topics.find((t) => t.id === state.topic) ?? data?.topics[0];
  const trendTopic = data?.topics.find((t) => t.id === trend);
  return (
    <PortraitExportContext.Provider
      value={{ example: false, asOf: data?.asOf ?? "" }}
    >
      <div className="space-y-5">
        <div className="rounded-lg border border-admin-card-border bg-admin-card p-4 text-sm">
          <strong>Personvern:</strong> Helsestatistikk vises bare for grupper
          med minst 20 personer med relevante data. Antall 1–4 og andre verdier
          som kan avsløre små grupper skjermes.
          <p className="mt-2 text-xs text-admin-text-secondary">
            {data?.health.notice ??
              "Skjerming håndheves på serveren. Ingen personrader eller fritekstsvar sendes til nettleseren."}
          </p>
          {data && (
            <p className="mt-2 text-xs text-admin-warn-400">
              {data.health.consent}
            </p>
          )}
        </div>
        <FiltersCard>
          <div className="flex flex-wrap items-start gap-3">
            <Field label="Kundegruppe" info={STATUS}>
              <Select
                value={population}
                onChange={(e) => update({ population: e.target.value })}
              >
                <option value="">Alle grupper</option>
                <option value="active">Aktive privatabonnementer</option>
                <option value="churned">Tidligere privatabonnementer</option>
                <option value="company">Sponset, uten betalt abonnement</option>
                <option value="one_time">
                  Øvrig privat, uten betalt abonnement
                </option>
                <option value="lead">Leads</option>
              </Select>
            </Field>
            <Field label="Privat / bedrift" info={OVERLAP}>
              <Select
                value={relationship}
                onChange={(e) =>
                  update({ relationship: e.target.value, tier: "" })
                }
              >
                <option value="">Alle relasjoner</option>
                <option value="private">Privat (inkl. begge)</option>
                <option value="company">Bedrift (inkl. begge)</option>
                <option value="both">Begge</option>
              </Select>
            </Field>
            <Field
              label="Privat abonnementsmodell"
              info="Aktiv betalt modell, ellers siste betalte modell. Privatbesøk uten betalt abonnement vises som «Annet / ukjent»."
            >
              <Select
                value={tier}
                onChange={(e) => update({ tier: e.target.value })}
              >
                <option value="">Alle modeller</option>
                {TIERS.map((v) => (
                  <option key={v}>{v}</option>
                ))}
              </Select>
            </Field>
            <Field label="Kjønn">
              <Select
                value={gender}
                onChange={(e) => update({ gender: e.target.value })}
              >
                <option value="">Alle</option>
                {GENDERS.map((v) => (
                  <option key={v}>{v}</option>
                ))}
              </Select>
            </Field>
            <Field
              label="Aldersgrunnlag"
              info="Første kundedato for kunder, registreringsdato for leads. Tidsvisningen bruker alltid alder ved inngang."
            >
              <Select
                value={tab === "time" ? "entry" : ageBasis}
                disabled={tab === "time"}
                onChange={(e) => update({ ageBasis: e.target.value })}
              >
                <option value="today">Alder i dag</option>
                <option value="entry">Alder ved inngang</option>
              </Select>
            </Field>
            <Field label="Aldersgruppe">
              <Select
                value={age}
                onChange={(e) => update({ age: e.target.value })}
              >
                <option value="">Alle aldre</option>
                {AGE_BANDS.map((v) => (
                  <option key={v}>{v}</option>
                ))}
              </Select>
            </Field>
            <Button
              variant="ghost"
              size="sm"
              className="mt-5"
              onClick={() =>
                update({
                  population: "",
                  relationship: "",
                  tier: "",
                  gender: "",
                  age: "",
                })
              }
            >
              Nullstill filtre
            </Button>
          </div>
          <div className="mt-3 flex flex-wrap items-end gap-3">
            <Field label="Fra inngangsmåned (tidsvisning)">
              <MonthSelect
                value={from}
                onChange={(value) => update({ from: value })}
              />
            </Field>
            <Field label="Til inngangsmåned (tidsvisning)">
              <MonthSelect
                value={to}
                onChange={(value) => update({ to: value })}
              />
            </Field>
            <p className="pb-2 text-xs text-admin-text-subtle">
              Perioden gjelder bare «Endringer over tid». Oversikten viser hele
              valgt kundegruppe.
            </p>
          </div>
        </FiltersCard>
        {query.isLoading && <p role="status">Henter aggregater…</p>}
        {query.isError && (
          <div
            role="alert"
            className="rounded-lg border border-admin-danger-400 p-4"
          >
            <p>
              Kunne ikke hente kundebildet. Kontroller tilgang og velg en gyldig
              periode på inntil 61 måneder.
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void query.refetch()}
            >
              Prøv igjen
            </Button>
          </div>
        )}
        {data?.status === "too_small" && (
          <p
            role="status"
            className="rounded-lg border border-admin-card-border p-4"
          >
            For få personer til å vise data — minimum 20. Prøv færre filtre.
            Nøyaktig antall vises ikke.
          </p>
        )}
        {data?.status === "ready" && (
          <Tabs value={tab} onValueChange={(value) => update({ tab: value })}>
            <TabsList className="h-auto flex-wrap">
              <TabsTrigger value="overview">Oversikt</TabsTrigger>
              <TabsTrigger value="needs">Behov og sammenligninger</TabsTrigger>
              <TabsTrigger value="time">Endringer over tid</TabsTrigger>
            </TabsList>
            <TabsContent value="overview" className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                <Stat
                  title="Personer i utvalget"
                  value={`≈ ${display(data.summary.population)}`}
                  note="Hver person telles én gang · avrundet"
                  info={OVERLAP}
                  icon={<Users className="h-4 w-4" />}
                />
                <Stat
                  title="Besvart helsemål"
                  value={display(data.summary.answered?.percent, true)}
                  note={valueNote(data.summary.answered)}
                  info="Andel unike personer med minst ett besvart helsemål, ikke antall valgte mål. Maksimalt 100 %. Grunnlaget er publiserbare faste grupper."
                  icon={<Info className="h-4 w-4" />}
                />
                <Stat
                  title="Wearables tilkoblet"
                  value={display(data.summary.wearables?.percent, true)}
                  note={valueNote(data.summary.wearables)}
                  info="Registrert Garmin-tilkobling ELLER minst én wearable-dag fra Garmin, Apple Health eller Health Connect. Hver person telles én gang. Historiske data beviser ikke aktiv tilkobling."
                  icon={<Watch className="h-4 w-4" />}
                />
                <Stat
                  title="Lastet opp blodprøver"
                  value={display(data.summary.selfReported?.percent, true)}
                  note={valueNote(data.summary.selfReported)}
                  info="Minst ett besøk med self_reported = true, inkludert manuelt registrerte verdier. Hver person telles én gang."
                  icon={<FlaskConical className="h-4 w-4" />}
                />
              </div>
              <div className="grid gap-4 lg:grid-cols-2">
                <Distribution
                  title={
                    ageBasis === "entry" ? "Alder ved inngang" : "Alder i dag"
                  }
                  info="Andel av utvalget, inkludert ukjent alder. Ugyldig eller manglende fødselsdato blir ukjent."
                  rows={data.distributions.age ?? []}
                />
                <Distribution
                  title="Kjønn"
                  info="Registrert kjønn i siste profil, ellers ukjent."
                  rows={data.distributions.gender ?? []}
                />
                <Distribution
                  title="Privat og bedrift"
                  info={OVERLAP}
                  rows={data.distributions.membership ?? []}
                />
                <Distribution
                  title="Private abonnementsmodeller"
                  info="Aktiv betalt modell, ellers siste betalte modell. Grunnlag: personer med privat relasjon, inkludert begge."
                  rows={data.distributions.tier ?? []}
                />
                <Distribution
                  title="Vanligste helsemål"
                  info={ANSWERS}
                  rows={(data.topics.find((t) => t.id === "goals")?.rows ?? [])
                    .filter((r) => r.total.count !== null)
                    .sort((a, b) => b.total.count! - a.total.count!)
                    .slice(0, 6)
                    .map((r) => ({ label: r.label, ...r.total }))}
                  subtitle="Topp 6 blant publiserbare alternativer; alle alternativer i Behov"
                />
                <Distribution
                  title="Tid fra første betaling til i dag"
                  info="Første gjenopprettede betalte abonnementsstart / privatbetalte besøk eller sponsede kundedato til i dag. Begge relasjoner bruker tidligste dato. Kalenderperiode, ikke sammenhengende betalt medlemskap. Leads utelatt."
                  rows={data.distributions.tenure ?? []}
                />
              </div>
              <HealthProfile data={data} />
            </TabsContent>
            <TabsContent value="needs" className="space-y-4">
              <div className="flex flex-wrap items-end gap-3">
                <Field label="Kategori / spørsmål">
                  <Select
                    value={topic?.id ?? state.topic}
                    onChange={(e) => update({ topic: e.target.value })}
                  >
                    {data.topics.map((t) => (
                      <option value={t.id} key={t.id}>
                        {t.category}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label="Sammenlign etter">
                  <Select
                    value={compare}
                    onChange={(e) => update({ compare: e.target.value })}
                  >
                    <option value="age">Aldersgruppe</option>
                    <option value="gender">Kjønn</option>
                    <option value="membership">Privat / bedrift</option>
                  </Select>
                </Field>
                <Field label="Vis">
                  <Select
                    value={state.measure}
                    onChange={(e) => update({ measure: e.target.value })}
                  >
                    <option value="percent">Andel (%)</option>
                    <option value="count">Antall personer</option>
                  </Select>
                </Field>
              </div>
              {topic ? (
                <Needs topic={topic} measure={state.measure} />
              ) : (
                <p>Ingen spørsmål i katalogen.</p>
              )}
            </TabsContent>
            <TabsContent value="time" className="space-y-4">
              <div className="flex flex-wrap items-end gap-3">
                <Field label="Se utvikling i">
                  <Select
                    value={trend}
                    onChange={(e) =>
                      update({ trend: e.target.value, trendOption: "" })
                    }
                  >
                    <option value="age">Alder ved inngang</option>
                    <option value="gender">Kjønn</option>
                    <option value="membership">
                      Privat / bedrift (siste kjente)
                    </option>
                    {data.topics.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.category} ved inngang
                      </option>
                    ))}
                  </Select>
                </Field>
                {trendTopic && (
                  <Field label="Svaralternativ">
                    <Select
                      className="max-w-80"
                      value={data.monthly[0]?.option ?? ""}
                      onChange={(e) => update({ trendOption: e.target.value })}
                    >
                      {trendTopic.options.map((v) => (
                        <option key={v}>{v}</option>
                      ))}
                    </Select>
                  </Field>
                )}
                <Field label="Vis">
                  <Select
                    value={state.measure}
                    onChange={(e) => update({ measure: e.target.value })}
                  >
                    <option value="percent">Andel (%)</option>
                    <option value="count">Antall personer</option>
                  </Select>
                </Field>
              </div>
              <Monthly
                rows={data.monthly}
                measure={state.measure}
                needs={Boolean(trendTopic)}
                from={from}
                to={to}
              />
            </TabsContent>
          </Tabs>
        )}
        {data && (
          <p className="text-xs text-admin-text-subtle">
            Beregnet {data.asOf}. Kun aggregater · ingen individvisning · ingen
            migrasjoner.
          </p>
        )}
      </div>
    </PortraitExportContext.Provider>
  );
}
